<template>
    <div class="ui-page">
        <ui-page-header title="Sessão pública" subtitle="Acompanhamento da abertura das urnas, do resultado provisório e da rodada de lances de cada etapa. A abertura é conduzida pelos custódios no site de ativos; nenhuma chave passa pelo back-office (D2).">
            <v-btn outlined :disabled="!processId" :loading="loading" @click="load"><v-icon left small>mdi-refresh</v-icon>Atualizar</v-btn>
        </ui-page-header>

        <ui-table-card>
            <template #toolbar>
                <competitive-process-picker v-model="processId" />
            </template>
            <div v-if="!processId" class="ui-small ui-muted pa-4">Selecione um processo.</div>
            <div v-else-if="data && !data.stages.length" class="ui-small ui-muted pa-4">Nenhuma etapa cadastrada.</div>
        </ui-table-card>

        <ui-section v-for="stage in stages" :key="stage.stage_id" :title="`Etapa ${stage.number} · ${stage.name}`" :description="`Sessão marcada para ${formatSchedule(stage.opening_at)}`" class="mt-4">
            <template v-if="stage.urn && stage.urn.opened_at" #actions>
                <v-btn small outlined :loading="reporting === stage.stage_id" title="Relatório técnico de evidências da etapa (D9)" @click="evidenceReport(stage)"><v-icon left small>mdi-file-certificate-outline</v-icon>Relatório técnico</v-btn>
                <v-btn small outlined class="ml-2" :loading="downloading === stage.stage_id" @click="downloadMinutes(stage)"><v-icon left small>mdi-file-document-outline</v-icon>Baixar ata</v-btn>
            </template>
            <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px;">
                <ui-badge v-if="!stage.urn" text="Sem urna" tone="neutral" />
                <ui-badge v-else-if="stage.urn.opened_at" :text="`Aberta em ${formatStamp(stage.urn.opened_at, { seconds: true })}`" tone="success" />
                <ui-badge v-else-if="stage.session" text="Sessão em andamento" tone="warning" />
                <ui-badge v-else-if="stage.urn.closed_at" text="Fechada, aguardando a sessão" tone="neutral" />
                <ui-badge v-else text="Recebendo propostas" tone="neutral" />
                <span v-if="stage.urn && stage.urn.closed_at" class="ui-small">{{ stage.urn.envelope_count }} envelope(s) no conjunto · {{ stage.urn.threshold }} de {{ stage.urn.total_shares }} custódios abrem</span>
            </div>

            <div v-if="stage.session" class="ui-small mb-2">
                Conduzida por <strong>{{ stage.session.host_name }}</strong>, iniciada em {{ formatStamp(stage.session.started_at, { seconds: true }) }}<span v-if="stage.session.restarts"> (reiniciada {{ stage.session.restarts }}×)</span>.
                Impressão digital da sessão <span class="ui-mono">{{ shortFingerprint(stage.session.fingerprint) }}</span>.
                <div class="mt-1">
                    Fragmentos entregues: {{ stage.session.delivered.length }} de {{ stage.urn.threshold }}<span v-if="stage.session.delivered.length"> —
                    {{ stage.session.delivered.map((item) => `${item.name} (${item.share_index})`).join(", ") }}</span>.
                </div>
            </div>
            <div v-else-if="stage.urn && !stage.urn.opened_at && stage.start_blocker" class="ui-small ui-muted mb-2">{{ stage.start_blocker }}</div>

            <v-simple-table v-if="stage.results" dense>
                <thead><tr><th>Item</th><th>Proponente</th><th>Protocolo</th><th class="text-right">Valor</th><th>Pagamento</th><th>Situação</th></tr></thead>
                <tbody>
                    <template v-for="item in stage.results">
                        <tr v-if="!item.offers.length" :key="`empty-${item.stage_item_id}`">
                            <td><strong>{{ item.code }}</strong> · {{ item.name }}<div class="ui-cell-sub">mínimo {{ formatMoney(item.minimum_value) }}</div></td>
                            <td colspan="5" class="ui-muted">{{ item.blocked ? "Item fora da etapa" : "Sem proposta: passa para a etapa seguinte" }}</td>
                        </tr>
                        <tr v-for="(offer, index) in item.offers" :key="`${item.stage_item_id}-${offer.protocol}`">
                            <td v-if="index === 0" :rowspan="item.offers.length"><strong>{{ item.code }}</strong> · {{ item.name }}<div class="ui-cell-sub">mínimo {{ formatMoney(item.minimum_value) }}</div></td>
                            <td>{{ offer.proponent || "—" }}<div class="ui-cell-sub">{{ offer.registration_protocol }}</div></td>
                            <td class="ui-mono ui-small">{{ offer.protocol }}</td>
                            <td class="text-right ui-nowrap">{{ formatMoney(offer.value) }}</td>
                            <td class="ui-small">{{ offer.payment || "—" }}</td>
                            <td>
                                <ui-badge v-if="offer.rank === 1" text="1º lugar" tone="success" />
                                <ui-badge v-else-if="offer.rank" :text="`${offer.rank}º lugar`" tone="neutral" />
                                <ui-badge v-else :text="offer.status_label" tone="warning" />
                            </td>
                        </tr>
                    </template>
                </tbody>
            </v-simple-table>

            <template v-if="stage.rounds && stage.rounds.length">
                <div class="ui-section-title mt-4 mb-1">Rodada de lances</div>
                <div class="ui-small ui-muted mb-2">{{ stage.bid_duration_minutes }} min por item · incremento {{ stage.bid_increment ? formatMoney(stage.bid_increment) : "1% da maior proposta" }} · lance nos 2 minutos finais prorroga por mais 2.</div>
                <v-simple-table dense>
                    <thead><tr><th>Item</th><th>Situação</th><th>Horário</th><th class="text-right">Abertura</th><th class="text-right">Maior valor</th><th>Lances</th><th>Termo de proposta final</th></tr></thead>
                    <tbody>
                        <tr v-for="round in stage.rounds" :key="round.id">
                            <td><strong>{{ round.code }}</strong> · {{ round.name }}<div class="ui-cell-sub">{{ round.participants }} participante(s)</div></td>
                            <td>
                                <ui-badge :text="round.status_label" :tone="round.status === 'running' ? 'warning' : round.status === 'closed' ? 'success' : 'neutral'" />
                                <div v-if="round.extensions" class="ui-cell-sub">{{ round.extensions }} prorrogação(ões)</div>
                            </td>
                            <td class="ui-small ui-nowrap">
                                <template v-if="round.starts_at">{{ formatStamp(round.starts_at) }} – {{ formatStamp(round.ends_at) }}</template>
                                <span v-else class="ui-muted">{{ round.contested ? "após a rodada anterior" : "—" }}</span>
                            </td>
                            <td class="text-right ui-nowrap">{{ formatMoney(round.opening_value) }}<div class="ui-cell-sub">{{ round.opening_holder }}</div></td>
                            <td class="text-right ui-nowrap">
                                <template v-if="round.winner"><strong>{{ formatMoney(round.winner.value) }}</strong><div class="ui-cell-sub">Vencedor: {{ round.winner.holder }}</div></template>
                                <template v-else>{{ formatMoney(round.best_value) }}<div class="ui-cell-sub">{{ round.best_holder }}</div></template>
                            </td>
                            <td class="ui-small">{{ round.bid_count }}</td>
                            <td class="ui-small">
                                <div v-for="result in round.results" :key="result.id" class="mb-1">
                                    <ui-badge :text="result.status_label" :tone="RESULT_TONE[result.status]" />
                                    <div class="ui-cell-sub">{{ result.position }}º · {{ result.holder }} · {{ formatMoney(result.value) }}</div>
                                    <div v-if="result.status === 'awaiting_signature'" class="ui-cell-sub">prazo até {{ formatStamp(result.deadline_at) }}</div>
                                    <div v-else-if="result.status === 'signed'" class="ui-cell-sub">
                                        assinado em {{ formatStamp(result.signed_at) }} ·
                                        <a href="#" @click.prevent="openSignature(result.signature_id)">PDF assinado</a>
                                    </div>
                                </div>
                                <span v-if="!round.results.length" class="ui-muted">—</span>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </template>
        </ui-section>
    </div>
</template>

<script>
import axios from "axios";
import CompetitiveProcessPicker from "@/components/competitive/CompetitiveProcessPicker.vue";
import processScope from "@/components/competitive/process-scope";
import { API, formatMoney, formatSchedule, formatStamp, notifyError, notifySuccess, notifyWarning, openUrl } from "@/components/competitive/competitive.util";

export default {
    name: "CompetitivePublicSession",
    components: { CompetitiveProcessPicker },
    mixins: [processScope],
    data: () => ({
        data: null,
        loading: false,
        downloading: null,
        reporting: null,
        timer: null,
        RESULT_TONE: { awaiting_signature: "warning", signed: "success", lapsed: "danger" },
    }),
    mounted() {
        // Com rodada agendada ou em andamento, atualiza sozinho a cada 5 s.
        this.timer = setInterval(() => {
            const live = this.stages.some((stage) => (stage.rounds || []).some((round) => round.status !== "closed" || (round.result && round.result.status === "awaiting_signature")));
            if (live && !this.loading) this.load(true);
        }, 5000);
    },
    beforeDestroy() {
        clearInterval(this.timer);
    },
    computed: {
        stages() {
            return this.data ? this.data.stages : [];
        },
    },
    methods: {
        formatMoney,
        formatSchedule,
        formatStamp,
        async openSignature(id) {
            try {
                const result = await axios.get(`${API}/signatures/${id}/file`);
                window.open(result.data.content.url, "_blank", "noopener");
            } catch (error) {
                notifyError(this, error);
            }
        },
        // Ata da etapa (5c3): PDF com os termos assinados e a decisão do juízo anexos.
        // Parcial enquanto houver rodada aberta, termo por assinar ou item sem decisão.
        async downloadMinutes(stage) {
            this.downloading = stage.stage_id;
            try {
                const result = await axios.get(`${API}/stages/${stage.stage_id}/minutes`, { responseType: "blob" });
                const disposition = result.headers["content-disposition"] || "";
                const match = disposition.match(/filename="([^"]+)"/);
                const name = match ? match[1] : `ata-etapa-${stage.number}.pdf`;
                const url = URL.createObjectURL(result.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = name;
                link.click();
                URL.revokeObjectURL(url);
                if (name.startsWith("ata-parcial")) notifyWarning(this, "Ata parcial: a etapa ainda tem pendências, listadas no início do PDF.");
            } catch (error) {
                // Com responseType blob, a mensagem da API chega como Blob.
                if (error.response && error.response.data instanceof Blob) {
                    try { error.response.data = JSON.parse(await error.response.data.text()); } catch (ignored) { /* mantém o erro original */ }
                }
                notifyError(this, error);
            } finally {
                this.downloading = null;
            }
        },
        // Relatorio tecnico da etapa (D9): gerado sozinho depois da abertura e da homologacao;
        // aqui gera a versao dos registros atuais, ou abre a mesma se nada mudou.
        async evidenceReport(stage) {
            this.reporting = stage.stage_id;
            try {
                const report = (await axios.post(`${API}/stages/${stage.stage_id}/evidence-report`)).data.content;
                const file = await axios.get(`${API}/evidence-reports/${report.id}/file`);
                openUrl(file.data.content.url);
                notifySuccess(this, `Relatório ${report.public_id}, versão ${report.version}.`);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.reporting = null;
            }
        },
        shortFingerprint(hex) {
            return String(hex || "").slice(0, 32).toUpperCase().replace(/(.{4})(?=.)/g, "$1 ");
        },
        onProcess() {
            this.data = null;
            this.load();
        },
        async load(quiet = false) {
            if (!this.processId) return;
            if (!quiet) this.loading = true;
            try {
                const result = await axios.get(`${API}/processes/${this.processId}/public-session`);
                this.data = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss">
@import "../../components/competitive/competitive";
</style>
